/*
  <kg-product-promo>: Aktions-Ticket am Produkt. Klick auf den Code kopiert ihn,
  der Button haengt ihn an den Warenkorb (oder loest ihn wieder). Der Preis mit
  Code steht nicht im HTML und wird erst nach der ersten Interaktion des
  Besuchers berechnet, damit Suchmaschinen keinen zweiten Preis lesen.
*/

import { formatMoney } from './money.js';

const root = () => window.Shopify?.routes?.root || '/';
/*
  Kein 'scroll' in der Liste: Nach einem Neuladen stellt der Browser die alte
  Scrollposition von selbst wieder her, das loest ein scroll-Ereignis aus, ohne dass
  jemand etwas getan hat. Echte Besucher erreicht man trotzdem, denn Mausrad, Finger,
  Zeiger und Tastatur stehen weiter in der Liste.
*/
const REVEAL_EVENTS = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'wheel'];
const isBot = () => Boolean(navigator.webdriver) || /bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent);
/* Einmal aufgedeckt, bleibt aufgedeckt: Nachgeladene Bereiche wie der Schnellkauf-Drawer
   kaemen sonst nie dran, weil das Signal schon vorbei ist. */
let schonAufgedeckt = false;

const MERKMAL = "kgInteracted";

/*
  Gemeinsames Merkmal am Dokument: Sobald irgendwo eine Eingabe kam, steht
  data-kg-interacted am html-Element. Nachgeladene Bereiche bringen ihr Skript erst
  danach mit und lesen daran ab, dass der Besucher laengst da war.
*/
const schonEingegeben = () => MERKMAL in document.documentElement.dataset;

function merken() {
  document.documentElement.dataset[MERKMAL] = "";
}

if (!isBot()) {
  REVEAL_EVENTS.forEach((type) => window.addEventListener(type, merken, { capture: true, passive: true, once: true }));
}

async function request(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.description || data.message || `Cart ${response.status}`);
  return data;
}

class KgProductPromo extends HTMLElement {
  connectedCallback() {
    this.code = (this.dataset.code || '').trim().toUpperCase();
    this.percent = Number(this.dataset.percent || 0);
    this.price = Number(this.dataset.price || 0);
    this.priceLine = this.querySelector('[data-promo-price]');
    this.applyButton = this.querySelector('[data-promo-apply], [data-promo-add]');
    this.applyHome = this.applyButton?.parentElement || null;
    this.applySlot = this.querySelector('[data-promo-apply-slot]');
    this.form = this.closest('kg-product-form');

    this.onVariant = (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;
      this.price = Number(variant.price);
      this.renderPrice();
      const add = this.querySelector('[data-promo-add]');
      if (add) add.disabled = !variant.available;
    };
    this.form?.addEventListener('variant:change', this.onVariant);
    this.onCart = (event) => this.syncState(event.detail?.cart);
    document.addEventListener('cart:updated', this.onCart);
    this.addEventListener('click', (event) => {
      const add = event.target.closest('[data-promo-add]');
      if (event.target.closest('[data-promo-copy]')) this.copy();
      else if (event.target.closest('[data-promo-apply]')) this.toggle();
      else if (add) this.addQuantity(Number(add.dataset.promoAdd));
    });

    if (this.priceLine && !isBot()) this.armReveal();
    if (this.querySelector('[data-promo-apply]')) this.loadState();
    if (this.applyButton && this.applySlot) {
      this.placeApply();
      this.resizeObserver = new ResizeObserver(() => this.placeApply());
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('variant:change', this.onVariant);
    document.removeEventListener('cart:updated', this.onCart);
    this.resizeObserver?.disconnect();
    this.disarm();
  }

  /* „Anwenden“ bleibt neben Code oder Preis, solange es in dieselbe Zeile passt; sonst wandert es ganz nach unten */
  placeApply() {
    if (this.placing) return;
    this.placing = true;
    const button = this.applyButton;
    if (button.parentElement !== this.applyHome) this.applyHome.append(button);
    this.applySlot.hidden = true;
    const first = [...this.applyHome.children].find((child) => child !== button);
    const wrapped = first && button.offsetTop > first.offsetTop + first.offsetHeight / 2;
    if (wrapped) {
      this.applySlot.append(button);
      this.applySlot.hidden = false;
    }
    this.placing = false;
  }

  /* Preis mit Code erst zeigen, wenn ein Mensch die Seite bedient */
  armReveal() {
    if (schonAufgedeckt || schonEingegeben()) {
      this.revealed = true;
      this.renderPrice();
      return;
    }
    this.reveal = () => {
      this.disarm();
      schonAufgedeckt = true;
      this.revealed = true;
      this.renderPrice();
    };
    REVEAL_EVENTS.forEach((type) => window.addEventListener(type, this.reveal, { capture: true, passive: true }));
  }

  disarm() {
    if (!this.reveal) return;
    REVEAL_EVENTS.forEach((type) => window.removeEventListener(type, this.reveal, { capture: true }));
    this.reveal = null;
  }

  renderPrice() {
    if (!this.revealed || !this.priceLine || !this.price || !this.percent) return;
    const discounted = Math.round((this.price * (100 - this.percent)) / 100);
    const format = (cents) => formatMoney(cents, this.dataset.moneyFormat, this.dataset.currency, document.documentElement.lang);
    const label = this.priceLine.querySelector('.visually-hidden');
    const current = document.createElement('strong');
    current.textContent = format(discounted);
    if (this.dataset.unit) {
      const unit = document.createElement('span');
      unit.className = 'product-promo__unit';
      unit.textContent = this.dataset.unit;
      current.append(unit);
    }
    const nodes = label ? [label, current] : [current];
    if (!this.hasAttribute('data-hide-compare')) {
      const compare = document.createElement('s');
      compare.textContent = format(this.price);
      nodes.push(compare);
    }
    this.priceLine.replaceChildren(...nodes);
    this.priceLine.classList.add('is-visible');
  }

  async copy() {
    const chip = this.querySelector('[data-promo-copy]');
    const text = chip?.querySelector('[data-promo-code-text]');
    if (!chip || !text) return;
    try {
      await navigator.clipboard.writeText(this.code);
    } catch {
      /* Zwischenablage gesperrt: Code wenigstens markieren */
      const range = document.createRange();
      range.selectNodeContents(text);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      return;
    }
    const original = this.code;
    text.textContent = this.dataset.copied || original;
    chip.classList.add('is-copied');
    window.clearTimeout(this.copyTimer);
    this.copyTimer = window.setTimeout(() => {
      text.textContent = original;
      chip.classList.remove('is-copied');
    }, 2000);
  }

  async loadState() {
    try {
      const cart = await (await fetch(`${root()}cart.js`)).json();
      this.syncState(cart);
    } catch {
      /* Zustand bleibt „nicht aktiv“ */
    }
  }

  syncState(cart) {
    if (!cart?.discount_codes || !this.querySelector('[data-promo-apply]')) return;
    this.setActive(cart.discount_codes.some((entry) => entry.code.toUpperCase() === this.code));
  }

  setActive(active) {
    this.active = active;
    this.classList.toggle('is-active', active);
    this.querySelector('[data-promo-apply]')?.setAttribute('aria-pressed', String(active));
  }

  /* Mengenaktion: die Mindestmenge ueber das Produktformular in den Warenkorb legen, der Code folgt im Warenkorb automatisch */
  addQuantity(quantity) {
    const form = this.form?.querySelector('[data-product-form]');
    const input = form?.querySelector('input[name="quantity"]');
    if (!form || !input || !quantity) return;
    input.value = quantity;
    form.requestSubmit();
    input.value = 1;
  }

  /* Code an den Warenkorb haengen oder wieder loesen; der Warenkorb-Drawer bekommt seine Section gleich mit */
  async toggle() {
    if (this.busy || !this.applyButton) return;
    this.busy = true;
    this.applyButton.setAttribute('aria-busy', 'true');
    try {
      const cart = await (await fetch(`${root()}cart.js`)).json();
      const others = (cart.discount_codes || []).map((entry) => entry.code.toUpperCase()).filter((code) => code !== this.code);
      const next = this.active ? others : [...others, this.code];
      const drawer = document.querySelector('kg-cart');
      const sections = drawer?.sectionId ? { sections: drawer.sectionId, sections_url: drawer.url } : {};
      const updated = await request(`${root()}cart/update.js`, { discount: next.join(','), ...sections });
      this.setActive(!this.active);
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: updated, source: 'promotion', sections: updated.sections } }));
      const openCart = () => {
        const target = document.querySelector('kg-cart[data-drawer]');
        if (target?.open) target.open();
        else window.location.href = `${root()}cart`;
      };
      if (this.active) window.kgToast?.(this.dataset.appliedToast, this.dataset.cartLabel, openCart, 6000);
      else window.kgToast?.(this.dataset.removedToast, undefined, undefined, 4000);
    } catch {
      window.kgToast?.(this.dataset.errorToast);
    } finally {
      this.busy = false;
      this.applyButton.removeAttribute('aria-busy');
    }
  }
}

customElements.define('kg-product-promo', KgProductPromo);
